import { useState } from 'react';
import type { OrderComment } from '@/infrastructure/FulfillmentAdminClient';
import { logTimestamp } from '../activityLog';
import { isLineMemo } from '../commentNote';

/** A mention is what turns a comment into a Slack alert, so it has to read as one on screen too. */
const MENTION = /(@[\w.-]+)/g;

/** Shared with the memo row under the first order line, so a mention reads the same in both places [SSD-7216]. */
export const withMentions = (text: string) =>
  text.split(MENTION).map((part, index) =>
    part.startsWith('@') ? (
      <span className="at" key={`${part}-${index}`}>
        {part}
      </span>
    ) : (
      part
    ),
  );

export interface OperatorCommentsProps {
  comments: OrderComment[];
  busy: boolean;
  onPost: (text: string) => Promise<void>;
}

/** The order's own thread, open with the order rather than hidden behind its button. */
export function OperatorComments({ comments, busy, onPost }: OperatorCommentsProps) {
  const [draft, setDraft] = useState('');

  const post = async () => {
    const text = draft.trim();
    if (!text || busy) {
      return;
    }
    await onPost(text);
    setDraft('');
  };

  return (
    <div className="cpanel">
      {comments.length === 0 && <div className="empty">No comments on this order yet.</div>}
      {comments.map((comment) => (
        <div className="c-item" key={`${comment.at}-${comment.author}-${comment.lineNo ?? ''}-${comment.text}`}>
          <span className="who">{comment.author}</span>
          {/* Only a memo whose line left the table reaches the pane; the tag says which line it was about. */}
          {isLineMemo(comment) && <span className="line-tag">Line {comment.lineNo}</span>}
          <span>{withMentions(comment.text)}</span>
          <time>{logTimestamp(comment.at)}</time>
        </div>
      ))}
      <div className="cwrite">
        <input
          aria-label="Write a comment"
          placeholder="Write a comment — @name sends an automatic Slack alert (order no · text · time · author)"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void post();
            }
          }}
        />
        <button
          type="button"
          className="btn btn-blue btn-sm"
          disabled={busy || !draft.trim()}
          onClick={() => void post()}
        >
          Post
        </button>
      </div>
    </div>
  );
}
